/**
 * StatsBox Component
 *
 * Editorial KPI tile — number-as-hero, no card backgrounds.
 * Visual language matches the Portfolio KpiStrip for consistency.
 *
 * Variants map to semantic colors in the number:
 *   draft      → gray-800  (neutral)
 *   ready      → primary-700
 *   mapped     → primary-700
 *   scheduled  → secondary-500
 *   published  → primary-700
 *
 * Layout expectations:
 *   Parent grid should add `border-r border-gray-900/8` on all-but-last
 *   tiles (or pass `hasDivider` to let StatsBox handle it internally).
 */

import { Icon } from '@/components/ui/Icon';

export interface StatsBoxProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon?: string;
  accentColor?: 'draft' | 'ready' | 'mapped' | 'scheduled' | 'published';
  isLoading?: boolean;
  onClick?: () => void;
  /** Render a hairline right-divider (for last-child exclusion in flex layouts) */
  hasDivider?: boolean;
  /** Extra classes on the root (e.g. `w-full` when the tile sits in a flex cell) */
  className?: string;
}

// Semantic number color per pipeline stage
const numColorMap: Record<string, string> = {
  draft:     'text-gray-800',
  ready:     'text-primary-700',
  mapped:    'text-primary-700',
  scheduled: 'text-gray-700',
  published: 'text-primary-700',
};

const StatsBoxSkeleton = ({ hasDivider, className = '' }: { hasDivider?: boolean; className?: string }) => (
  <div
    className={[
      'px-3 sm:px-5 py-4 sm:py-5 flex flex-col items-center text-center',
      'animate-pulse',
      hasDivider ? 'border-r border-gray-900/8' : '',
      className,
    ].join(' ')}
  >
    <div className="h-4 w-4 rounded bg-gray-100" />
    <div className="mt-[8px] flex h-[48px] items-center justify-center">
      <div className="h-9 w-10 rounded bg-gray-200" />
    </div>
    <div className="mt-[8px] flex h-[14px] items-start justify-center">
      <div className="h-2.5 w-14 rounded bg-gray-100" />
    </div>
  </div>
);

export const StatsBox = ({
  title,
  value,
  subtitle,
  icon,
  accentColor = 'draft',
  isLoading = false,
  onClick,
  hasDivider = false,
  className = '',
}: StatsBoxProps) => {
  if (isLoading) {
    return <StatsBoxSkeleton hasDivider={hasDivider} className={className} />;
  }

  const numColor = numColorMap[accentColor] ?? numColorMap.draft;

  const tileClass = [
    // Base layout — matches KpiStrip tile exactly
    'px-3 sm:px-5 py-4 sm:py-5',
    'flex flex-col items-center text-center',
    // Divider
    hasDivider ? 'border-r border-gray-900/8' : '',
    // Transitions
    'transition-colors duration-150',
    className,
  ].join(' ');

  const interactiveClass = onClick
    ? 'cursor-pointer hover:bg-gray-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-inset'
    : 'cursor-default';

  const content = (
    <>
      {/* Every tile reserves the same top slot, so all numbers share one line. */}
      <span className="flex h-4 shrink-0 items-center justify-center text-gray-500" aria-hidden="true">
        {icon && <Icon name={icon} size="sm" />}
      </span>

      {/* Number — the visual hero */}
      <span className={`mt-[8px] flex h-[48px] shrink-0 items-center justify-center font-wagner text-4xl sm:text-5xl leading-none tabular-nums tracking-tight ${numColor}`}>
        {value}
      </span>

      {/* Label */}
      <span className="mt-[8px] flex items-start justify-center text-[10px] font-medium uppercase leading-tight tracking-[0.15em] text-gray-500 sm:text-[11px]">
        {title}
      </span>

      {/* Subtitle (optional) */}
      {subtitle && (
        <span className="mt-1 text-[10px] leading-none text-gray-400">
          {subtitle}
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${tileClass} ${interactiveClass}`}
        aria-label={`${title}: ${value}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`${tileClass} ${interactiveClass}`}>
      {content}
    </div>
  );
};
